/**
 * Step-up authentication: prove a passkey, *recently*, before something
 * irreversible.
 *
 * Two routes need it — `POST /encryption/reset`, which bulk-deletes the user's
 * content, and `DELETE /user`. Both are irreversible **by construction**:
 * Cynosure is end-to-end encrypted and the server holds no decryptable key
 * material, so there is no backup anyone can restore from. That, not
 * sensitivity, is what earns these two routes a step-up while the rest of the
 * API is an ordinary bearer-token affair.
 *
 * The threat is specific. Someone holding a stolen access token can already do
 * everything an ordinary session can, and cannot read anything meaningful —
 * the API returns ciphertext and the key is device-local. What they *can* do is
 * call the one route that destroys. Step-up is what stops a stolen token from
 * being a data-destruction primitive.
 *
 * ## The proof
 *
 * Re-run the OIDC flow with `prompt=login`; the API then reads `amr` and
 * `auth_time` by introspection. Nothing travels in the request body.
 *
 * There used to be a second path here — Cynosure's own passkey ceremony,
 * yielding a short-lived `reauth_token`. It retired with the local issuer in
 * Phase 4, along with the `reauth_token` field on all three request schemas.
 * Worth remembering why it was a problem before it was deleted: as a REQUIRED
 * field whose only valid value came from a ceremony against Cynosure's RP ID,
 * it made both routes literally unconstructable for the IdP users the migration
 * was moving everyone to.
 */

import { Platform } from 'react-native';

import { isStepUpChallenge, maxAgeFromChallenge } from './challenge';
import type { StepUpReturnRoute } from './returnRoute';
import { startLogin } from './oidc';
import { completeOidcLogin } from './completeOidcLogin';

/** Raised when the user abandons the re-authentication, or it cannot run. */
export class StepUpError extends Error {}

// Re-exported so a caller that already imports `withStepUp` does not need a
// second import path. The definitions live in a dependency-free module because
// oidc.ts and app/auth/callback.tsx need them without this file's store chain.
export {
  STEP_UP_RETURN_ROUTES,
  isStepUpReturnRoute,
  type StepUpReturnRoute,
} from './returnRoute';

/**
 * Obtain whatever this session needs to pass a step-up.
 *
 * The proof travels in the access token itself, so there is nothing to return
 * and nothing for the caller to put in a request body — just retry the action.
 *
 * ⚠️  On **web**, an IdP step-up navigates the page away to the hosted login
 *     and this never returns — it throws to unwind the caller's stack while the
 *     browser is already leaving. Pass `returnTo` so the user comes back to the
 *     screen that asked, presses the button again, and that second press finds
 *     a fresh `auth_time`. Resuming the original action across a full page
 *     navigation would mean persisting and replaying an arbitrary destructive
 *     intent — a worse idea than asking the user to press the button again.
 *
 * ⚠️  CALL THE ACTION FIRST AND STEP UP ONLY ON THE SERVER'S CHALLENGE — see
 *     `needsReauth`. Calling this *before* the action is what made account
 *     reset, delete-account and export unreachable on web for three weeks: the
 *     redirect fires, the statement after `await stepUp()` never runs, and the
 *     retry re-enters this function instead of reaching the API. Nothing about
 *     that loop is visible in a server log, because no request is ever made.
 */
export async function stepUp(returnTo?: StepUpReturnRoute): Promise<void> {
  // Forces a real re-authentication at the IdP, which is what advances
  // auth_time. Without the prompt the IdP would happily reuse its existing
  // session and hand back a token whose auth_time is exactly as stale as the
  // one that failed.
  //
  // `returnTo` is what makes the web half survivable: it is persisted with the
  // PKCE record and read by app/auth/callback, so the browser comes back to the
  // screen that asked for the step-up rather than to '/'. Callers that pass
  // nothing get the old behaviour, which is correct for native.
  const started = await startLogin({ prompt: 'login', returnTo });

  if (!started) {
    if (Platform.OS === 'web') {
      // The page is navigating; nothing after this runs.
      throw new StepUpError('Redirecting to sign in again…');
    }
    throw new StepUpError('Re-authentication was cancelled');
  }

  // Reuses the ordinary login completion: exchange, re-establish the session,
  // then store. Re-establishing is a no-op for an account that already exists,
  // and keeping one path means a re-auth cannot drift from a first login.
  await completeOidcLogin(started.code);
}

/**
 * Run an action that may require a step-up, in the only order that works.
 *
 * ⚠️  THE ACTION GOES FIRST, and the step-up answers the server's challenge
 *     rather than pre-empting it. Every caller used to call `stepUp()` up front
 *     instead, which made account reset, delete-account and export unreachable
 *     on web for three weeks: `startLogin` redirects the page, so the statement
 *     after `await stepUp()` never ran, no request was ever made, and pressing
 *     the button again just redirected again. It lives here, once, rather than
 *     as a try/catch copied into three screens — which is how it drifted.
 *
 * On web `stepUp` never returns; it navigates and throws, so the retry below is
 * a native-only path and `returnTo` is what brings the browser back to the
 * screen that asked. The retry is deliberately singular: a token still too old
 * after a real re-authentication is a fault to report, not to spin on.
 */
export async function withStepUp<T>(
  action: () => Promise<T>,
  returnTo?: StepUpReturnRoute,
): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (!needsReauth(error)) throw error;
    await stepUp(returnTo);
    return await action();
  }
}

/**
 * Whether a failed request is asking for a step-up rather than reporting a
 * genuine authorization failure.
 *
 * Reads the RFC 9470 challenge — `401` with `WWW-Authenticate: Bearer
 * error="insufficient_user_authentication"` — rather than the bespoke
 * `X-Reauth-Required: prompt=login` header this used to send. Same two cases as
 * before, now in the registered form:
 *
 * | Server says | Meaning | Remedy |
 * |---|---|---|
 * | challenge **with** `max_age` | authentication is too old | re-authenticate; `stepUp()` |
 * | challenge **without** `max_age` | wrong method — a password session | sign in with a passkey |
 *
 * The distinction is kept because the remedies differ: telling someone who
 * already has a passkey to go and get one leaves them nothing to do. It is
 * expressed by the presence of `max_age` rather than by two different headers,
 * because RFC 9470 gives exactly one error code for both.
 */
export function needsReauth(error: unknown): boolean {
  return isStepUpChallenge(error) && maxAgeFromChallenge(error) !== null;
}
